import * as React from 'react'
import clsx from 'clsx'
import { ButtonProps, ButtonClasses, Button } from '../Button'
import { CircularProgress } from '../CircularProgress'
import { SxProps } from '../sx'
import { useId } from '../../hooks/useId'
import styles from './index.module.scss'
import { capitalize } from '../../utils'

export interface LoadingButtonClasses {
  /** Styles applied to the root element. */
  root: string
  /** Styles applied to the root element if `loading={true}`. */
  loading: string
  /** Styles applied to the loadingIndicator element. */
  loadingIndicator: string
  /** Styles applied to the loadingIndicator element if `loadingPosition="center"`. */
  loadingIndicatorCenter: string
  /** Styles applied to the loadingIndicator element if `loadingPosition="start"`. */
  loadingIndicatorStart: string
  /** Styles applied to the loadingIndicator element if `loadingPosition="end"`. */
  loadingIndicatorEnd: string
  /** Styles applied to the endIcon element if `loading={true}` and `loadingPosition="end"`. */
  endIconLoadingEnd: string
  /** Styles applied to the startIcon element if `loading={true}` and `loadingPosition="start"`. */
  startIconLoadingStart: string
}

interface LoadingButtonProps extends ButtonProps {
  classes?: Partial<ButtonClasses> & {
    /** Styles applied to the root element. */
    root?: string
    /** Styles applied to the root element if `loading={true}`. */
    loading?: string
    /** Styles applied to the loadingIndicator element. */
    loadingIndicator?: string
    /** Styles applied to the loadingIndicator element if `loadingPosition="center"`. */
    loadingIndicatorCenter?: string
    /** Styles applied to the loadingIndicator element if `loadingPosition="start"`. */
    loadingIndicatorStart?: string
    /** Styles applied to the loadingIndicator element if `loadingPosition="end"`. */
    loadingIndicatorEnd?: string
    /** Styles applied to the endIcon element if `loading={true}` and `loadingPosition="end"`. */
    endIconLoadingEnd?: string
    /** Styles applied to the startIcon element if `loading={true}` and `loadingPosition="start"`. */
    startIconLoadingStart?: string
  }
  /**
   * If `true`, the loading indicator is shown.
   * @default false
   */
  loading?: boolean
  /**
   * Element placed before the children if the button is in loading state.
   * The node should contain an element with `role="progressbar"` with an accessible name.
   * By default we render a `CircularProgress` that is labelled by the button itself.
   * @default <CircularProgress color="inherit" size={16} />
   */
  loadingIndicator?: React.ReactNode
  /**
   * The loading indicator can be positioned on the start, end, or the center of the button.
   * @default 'center'
   */
  loadingPosition?: 'start' | 'end' | 'center'
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
}

export const LoadingButton = React.forwardRef<
  HTMLButtonElement,
  LoadingButtonProps
>(function LoadingButton(props, ref) {
  const {
    classes,
    children,
    disabled = false,
    id: idProp,
    loading = false,
    loadingIndicator: loadingIndicatorProp,
    loadingPosition = 'center',
    variant = 'contained',
    fullWidth,
    size = 'medium',
    color = 'primary',
    ...other
  } = props

  const id = useId(idProp)
  const loadingIndicator = loadingIndicatorProp ?? (
    <CircularProgress
      aria-labelledby={id}
      color="inherit"
      size={16}
      thickness={5}
    />
  )

  const ownerState = {
    ...props,
    disabled,
    loading,
    loadingIndicator,
    loadingPosition,
    variant,
  }

  const loadingButtonLoadingIndicator = loading ? (
    <div
      className={clsx(
        [classes?.loadingIndicator, styles.loadingIndicator],
        styles[`loadingPosition-${loadingPosition}`],
        // @ts-ignore
        classes?.[`loadingIndicator${capitalize(loadingPosition)}`],
        styles[`variant-${variant}`],
        // @ts-ignore
        classes?.[`variant${capitalize(variant)}`],
        fullWidth && [styles.fullWidth],
        styles[`size-${size}`]
      )}
    >
      {loadingIndicator}
    </div>
  ) : null

  return (
    <Button
      disabled={disabled || loading}
      id={id}
      ref={ref}
      size={size}
      fullWidth={fullWidth}
      {...other}
      variant={variant}
      color={color}
      classes={{
        ...classes,
        root: clsx([
          styles.root,
          classes?.root,
          loadingPosition === 'center' &&
            styles[`loadingPosition-${loadingPosition}`],
          loading && [styles.loading],
          styles[`${color}--${variant}`],
        ]),
        endIcon: clsx(
          loading && [styles.endIconLoading, classes?.endIconLoadingEnd]
        ),
        startIcon: clsx(
          loading && [styles.startIconLoading, classes?.startIconLoadingStart]
        ),
        fullWidth: clsx([styles.fullWidth, classes?.fullWidth]),
      }}
    >
      {ownerState.loadingPosition === 'end'
        ? children
        : loadingButtonLoadingIndicator}
      {ownerState.loadingPosition === 'end'
        ? loadingButtonLoadingIndicator
        : children}
    </Button>
  )
})
